import { useCallback, useEffect, useMemo } from 'react'

import { Options, useQueryState } from 'nuqs'

import { useValueRef } from '../hook'
import { BaseStorageHookOptions, isUpdaterFn, UpdaterFn } from '../types'
import {
  getValidationSchemeFromConfig,
  StorableType,
  BaseValue,
  validateByScheme,
} from '../validation'
import { CreateParserOptions } from './createUrlParser'
import { getParserWithValidation } from './parsers'

type NuqsOptions = {
  /**
   * @default false
   */
  clearOnDefault?: boolean
}

type BehaviorOptions = {
  /**
   * @default true
   */
  writeDefaultValueOnMount?: boolean
}

export type UseUrlHookOptions<
  TType extends StorableType,
  TValue extends BaseValue<TType>,
> = BaseStorageHookOptions<TType, TValue> & CreateParserOptions & NuqsOptions & BehaviorOptions

export function useUrlStorageState<TType extends StorableType, TValue extends BaseValue<TType>>(
  key: string,
  options: UseUrlHookOptions<TType, TValue>,
) {
  const { defaultValue = null, writeDefaultValueOnMount = true } = options

  const { parser, scheme } = useUrlStorageStateOptions(options)

  const [value, _internalSetValue] = useQueryState<TValue>(key, parser)

  /**
   * Ref необходим, чтобы сделать сеттер-функцию (`setValue`) практически постоянной.
   * Избежать её изменения из-за options или internalValue
   */
  const valueRef = useValueRef(value)
  const optionRef = useValueRef(options)

  const internalSetValue = useCallback(
    (newValue: TValue | null, options?: Options) => {
      _internalSetValue(newValue, options)
      valueRef.current = newValue
    },
    [_internalSetValue, valueRef],
  )

  useEffect(() => {
    if (defaultValue === null || !writeDefaultValueOnMount) return

    const params = new URLSearchParams(window.location.search)

    if (!params.has(key)) {
      internalSetValue(defaultValue, { shallow: true, clearOnDefault: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * @returns
   * `true` – значение было установлено;
   * `false` – значение не прошло валидацию и не было установлено
   */
  const setValue = useCallback(
    (valueOrUpdater: TValue | UpdaterFn<TValue> | null) => {
      const oldValue = valueRef.current
      const options = optionRef.current

      const newValue = isUpdaterFn(valueOrUpdater) ? valueOrUpdater(oldValue) : valueOrUpdater

      if (newValue === null) {
        internalSetValue(null)
        return true
      }

      const { success, data, error } = validateByScheme<TValue>(newValue, scheme)

      if (!success) {
        options.onValidationError?.(error)
        return false
      }

      internalSetValue(data)
      return true
    },
    [internalSetValue, optionRef, scheme, valueRef],
  )

  return [value, setValue] as const
}

/**
 * @internal
 *
 * Вспомогательный хук. Подготавливает конфиг для основного
 */
function useUrlStorageStateOptions<TType extends StorableType, TValue extends BaseValue<TType>>(
  options: UseUrlHookOptions<TType, TValue>,
) {
  const {
    onValidationError,
    type,
    validation,
    defaultValue = null,
    clearOnDefault = false,
  } = options

  const scheme = useMemo(() => {
    return getValidationSchemeFromConfig(type, validation)
  }, [type, validation])

  const parser = useMemo(() => {
    return getParserWithValidation<TType, TValue>({
      clearOnDefault,
      defaultValue,
      scheme,
      type,
      onValidationError,
    })
  }, [clearOnDefault, defaultValue, onValidationError, scheme, type])

  return {
    scheme,
    parser,
  }
}
